import { useEffect, useMemo, useRef } from 'react';
import { type Tuning, tuningPrefersSharps } from '@theory/tunings';
import { midiToPitchClass, pitchClassToSemi } from '@util/midi';
import { INTERVAL_BY_SEMITONE } from '@theory/intervals';
import styles from './ScaleDiagram.module.css';

const VISIBLE_FRETS = 22;
const OPEN_ZONE = 30;
const CELL_WIDTH = 56;
const FRET_LABEL_AREA = 24;

const INLAY_FRETS_SINGLE = new Set([3, 5, 7, 9, 15, 17, 19, 21]);
const INLAY_FRETS_DOUBLE = new Set([12, 24]);

export interface CagedWindow {
  lowFret: number;
  highFret: number;
}

interface ScaleDiagramProps {
  tuning: Tuning;
  scaleNotes: string[];
  chordRoot: string;
  isLeftHanded?: boolean;
  cagedWindow?: CagedWindow | null;
  /** Semitone offsets relative to arpeggioRootNote that comprise the active
   *  arpeggio overlay. Empty/undefined means no overlay. */
  arpeggioSemitoneSet?: Set<number>;
  /** Absolute root for the arpeggio overlay (e.g. "C", "F#"). May differ from
   *  the scale root when the user picks a free-pick arpeggio. */
  arpeggioRootNote?: string;
  height?: number;
  fretCount?: number;
}

export function ScaleDiagram({
  tuning,
  scaleNotes,
  chordRoot,
  isLeftHanded = false,
  cagedWindow,
  arpeggioSemitoneSet,
  arpeggioRootNote,
  height = 275,
  fretCount = VISIBLE_FRETS,
}: ScaleDiagramProps) {
  // Drag-to-pan: when the SVG is wider than its scroll container, allow
  // grabbing anywhere on the fretboard and dragging horizontally to scroll.
  // Uses pointer events so the same code path covers mouse, touch, and pen.
  const frameRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    let pointerId: number | null = null;
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    const DRAG_THRESHOLD = 4;

    const onPointerDown = (e: PointerEvent) => {
      // Only react to primary button (or touch / pen)
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      dragging = false;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      const dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        el.setPointerCapture(e.pointerId);
        el.classList.add(styles.dragging);
      }
      el.scrollLeft = startScroll - dx;
      // Prevent text selection / accidental link activation while dragging.
      e.preventDefault();
    };

    const endDrag = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      if (dragging) {
        try { el.releasePointerCapture(e.pointerId); } catch { /* no-op */ }
        el.classList.remove(styles.dragging);
      }
      pointerId = null;
      dragging = false;
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('pointerleave', endDrag);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
      el.removeEventListener('pointerleave', endDrag);
    };
  }, []);

  const stringCount = tuning.midi.length;
  const sharps = tuningPrefersSharps(tuning);
  const stringAreaH = height - FRET_LABEL_AREA;
  const stringSpacing = stringAreaH / stringCount;
  const svgWidth = OPEN_ZONE + CELL_WIDTH * fretCount;
  const dotR = Math.min(stringSpacing, CELL_WIDTH) * 0.32;

  const scaleSet = useMemo(() => {
    const set = new Set<number>();
    scaleNotes.forEach((n) => set.add(pitchClassToSemi(n)));
    return set;
  }, [scaleNotes]);

  const rootSemi = pitchClassToSemi(chordRoot);

  const fretCenterX = (fret: number) =>
    fret === 0 ? OPEN_ZONE / 2 : OPEN_ZONE + CELL_WIDTH * (fret - 0.5);
  // tuning.midi is ordered low-pitch → high-pitch (index 0 = low E in standard).
  // Guitar diagrams show low strings at the BOTTOM and high strings at the TOP,
  // so the visual row for pitch index s is (stringCount - 1 - s).
  const stringY = (pitchIndex: number) =>
    stringSpacing * (stringCount - 1 - pitchIndex + 0.5);

  // CAGED shapes recur every 12 frets. When a window is active, build all
  // octave-shifted copies that fit within the visible fret range and treat
  // every one of them as "in focus" so a single CAGED selection outlines all
  // its instances across the neck.
  const cagedWindows = useMemo(() => {
    if (!cagedWindow) return [] as { lowFret: number; highFret: number }[];
    const all = [cagedWindow];
    for (let shift = 12; cagedWindow.highFret + shift <= fretCount; shift += 12) {
      all.push({ lowFret: cagedWindow.lowFret + shift, highFret: cagedWindow.highFret + shift });
    }
    return all;
  }, [cagedWindow, fretCount]);

  const inAnyWindow = (fret: number) =>
    cagedWindows.length === 0 || cagedWindows.some((w) => fret >= w.lowFret && fret <= w.highFret);
  const hasActiveWindow = cagedWindows.length > 0;

  const arpRootSemi = arpeggioRootNote ? pitchClassToSemi(arpeggioRootNote) : null;
  const hasArpeggio =
    arpeggioSemitoneSet !== undefined && arpeggioSemitoneSet.size > 0 && arpRootSemi !== null;

  // Build note positions: every in-scale note, plus arpeggio-only chromatic
  // notes (in arpeggio but not in scale) when an overlay is active.
  const notes = useMemo(() => {
    const out: {
      string: number;
      fret: number;
      noteName: string;
      noteSemi: number;
      colour: string;
      kind: 'scale' | 'arpeggio-only';
    }[] = [];
    for (let s = 0; s < stringCount; s++) {
      const openMidi = tuning.midi[s];
      for (let f = 0; f <= fretCount; f++) {
        const noteSemi = (openMidi + f) % 12;
        const inScale = scaleSet.has(noteSemi);
        const inArp =
          hasArpeggio && arpRootSemi !== null
            ? arpeggioSemitoneSet!.has(((noteSemi - arpRootSemi) + 12) % 12)
            : false;
        if (!inScale && !inArp) continue;
        const noteName = midiToPitchClass(openMidi + f, sharps);
        // Use arpeggio-relative colour when the dot only exists because of the
        // overlay (matches the mobile rendering for chromatic fill-in notes).
        const colourSemis = inScale
          ? ((noteSemi - rootSemi) + 12) % 12
          : ((noteSemi - (arpRootSemi ?? 0)) + 12) % 12;
        const colour =
          INTERVAL_BY_SEMITONE[colourSemis]?.colour ?? 'var(--colour-interval-other)';
        out.push({
          string: s,
          fret: f,
          noteName,
          noteSemi,
          colour,
          kind: inScale ? 'scale' : 'arpeggio-only',
        });
      }
    }
    return out;
  }, [scaleSet, stringCount, tuning, sharps, rootSemi, fretCount, hasArpeggio, arpeggioSemitoneSet, arpRootSemi]);

  return (
    <div
      ref={frameRef}
      className={[styles.frame, isLeftHanded && styles.mirrored].filter(Boolean).join(' ')}
    >
      <svg
        className={styles.svg}
        width={svgWidth}
        height={height}
        viewBox={`0 0 ${svgWidth} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Scale fretboard diagram"
      >
        {Array.from({ length: stringCount }).map((_, i) => (
          <line
            key={`str-${i}`}
            x1={0}
            y1={stringY(i)}
            x2={svgWidth}
            y2={stringY(i)}
            stroke="var(--colour-border)"
            strokeWidth={1}
          />
        ))}
        <line
          x1={OPEN_ZONE}
          y1={0}
          x2={OPEN_ZONE}
          y2={stringAreaH}
          stroke="var(--colour-text-primary)"
          strokeWidth={4}
        />
        {Array.from({ length: fretCount }).map((_, i) => {
          const f = i + 1;
          const x = OPEN_ZONE + CELL_WIDTH * f;
          return (
            <line
              key={`fret-${f}`}
              x1={x}
              y1={0}
              x2={x}
              y2={stringAreaH}
              stroke="var(--colour-border)"
              strokeWidth={1}
            />
          );
        })}

        {Array.from({ length: fretCount + 1 }).map((_, i) => {
          if (i === 0 || (!INLAY_FRETS_SINGLE.has(i) && !INLAY_FRETS_DOUBLE.has(i))) return null;
          const cx = fretCenterX(i);
          if (INLAY_FRETS_DOUBLE.has(i)) {
            return (
              <g key={`inlay-${i}`}>
                <circle cx={cx} cy={stringAreaH * 0.333} r={4} fill="var(--colour-surface-raise)" />
                <circle cx={cx} cy={stringAreaH * 0.667} r={4} fill="var(--colour-surface-raise)" />
              </g>
            );
          }
          return (
            <circle
              key={`inlay-${i}`}
              cx={cx}
              cy={stringAreaH / 2}
              r={4}
              fill="var(--colour-surface-raise)"
            />
          );
        })}

        {notes.map((n, i) => {
          const cx = fretCenterX(n.fret);
          const cy = stringY(n.string);
          const inWin = inAnyWindow(n.fret);
          const isRoot = n.noteSemi === rootSemi;
          // CAGED outlining only applies to scale notes inside any of the
          // repeated CAGED windows; arpeggio-only notes never inherit it.
          const cagedOutline = hasActiveWindow && inWin && n.kind === 'scale';

          // Arpeggio outer ring (separate concentric circle, mirrors mobile).
          // Yellow for scale notes that are also in the arpeggio; cyan for
          // chromatic notes the arpeggio implies that fall outside the scale.
          let arpRing: string | null = null;
          if (hasArpeggio && arpRootSemi !== null) {
            const offsetFromArp = ((n.noteSemi - arpRootSemi) + 12) % 12;
            if (arpeggioSemitoneSet!.has(offsetFromArp)) {
              arpRing =
                n.kind === 'scale'
                  ? 'var(--colour-arpeggio)'
                  : 'var(--colour-arpeggio-out-of-scale)';
            }
          }

          // CAGED dimming should NOT hide arpeggio-only chromatic dots —
          // they only exist because of the overlay.
          const dimmed = hasActiveWindow && !inWin && n.kind === 'scale';

          return (
            <g key={`note-${i}`} opacity={dimmed ? 0.25 : 1}>
              {arpRing && (
                <circle
                  cx={cx}
                  cy={cy}
                  r={dotR + 4}
                  fill="none"
                  stroke={arpRing}
                  strokeWidth={3}
                />
              )}
              <circle
                cx={cx}
                cy={cy}
                r={dotR}
                fill={n.colour}
                stroke={
                  cagedOutline
                    ? 'var(--colour-text-primary)'
                    : isRoot
                      ? 'var(--colour-text-primary)'
                      : 'none'
                }
                strokeWidth={cagedOutline ? 3 : isRoot ? 2 : 0}
              />
              <text
                x={cx}
                y={cy}
                fontSize={dotR * 0.85}
                fontFamily="var(--font-label-family)"
                fontWeight={700}
                fill="var(--colour-background)"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {n.noteName}
              </text>
            </g>
          );
        })}

        {[...INLAY_FRETS_SINGLE, ...INLAY_FRETS_DOUBLE]
          .filter((f) => f <= fretCount)
          .map((f) => (
            <text
              key={`fret-num-${f}`}
              x={fretCenterX(f)}
              y={stringAreaH + FRET_LABEL_AREA - 4}
              fontSize={11}
              fill="var(--colour-text-muted)"
              fontFamily="var(--font-label-family)"
              textAnchor="middle"
            >
              {f}
            </text>
          ))}

        {Array.from({ length: stringCount }).map((_, i) => {
          const tuningNote = midiToPitchClass(tuning.midi[i], sharps);
          return (
            <text
              key={`label-${i}`}
              x={4}
              y={stringY(i)}
              fontSize={10}
              fill="var(--colour-text-muted)"
              fontFamily="var(--font-mono-family)"
              dominantBaseline="middle"
            >
              {tuningNote}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
